import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Dialog } from '@artosera/ui';
import { usePeut } from '../../../auth/usePeut.js';
import { useElevation } from '../../../components/useElevation.js';
import { formaterDate, formaterOctets } from '../../../lib/format.js';
import { estLotFichiers, estLotImages } from '../../../lib/images.js';
import {
  essayerLot,
  exigeSecondFacteur,
  lancerLot,
  lotAnnule,
  lotEnTravail,
  lotScelle,
  obtenirLot,
  relancerLot,
  type LotReprise,
} from '../../../lib/reprise.js';
import pageStyles from '../../../styles/page.module.css';
import { LotCorrespondance } from './correspondance/LotCorrespondance.js';
import { LotImages } from './images/LotImages.js';
import { cleLots } from './LotsBloc.js';
import { etatAfficheLot, messageReprise, texteRaisonTravail } from './messages.js';
import { AnnulationBloc } from './plan/AnnulationBloc.js';
import { EcartementBloc } from './plan/EcartementBloc.js';
import { ProgressionTravail } from './plan/ProgressionTravail.js';
import { RapportVue } from './RapportVue.js';
import styles from './Reprise.module.css';

/** Les états d'où l'on peut encore essayer, et donc lancer. */
const ETATS_AVANT_LANCEMENT = ['depose', 'essaye', 'pret'];

/**
 * La fiche d'un lot : ses fichiers, les gestes (essayer, lancer, relancer), le
 * suivi de son travail et ses rapports. Tant que le lot est en file ou en
 * cours, elle se relit toutes les 3 secondes.
 */
export function LotFiche({ idGalerie, lotId }: { idGalerie: string; lotId: string }): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const elevation = useElevation();
  const [confirmation, setConfirmation] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // L'essai des morceaux d'un lot de dépôt part en fond : on le suit une fois lancé.
  const [essaiLance, setEssaiLance] = useState(false);

  const cle = ['console', 'galerie', idGalerie, 'reprise', 'lot', lotId];
  const requete = useQuery({
    queryKey: cle,
    queryFn: () => obtenirLot(idGalerie, lotId),
    refetchInterval: (r) => {
      const lot = r.state.data;
      if (!lot) return false;
      const morceauxEnEssai = essaiLance && lot.morceaux?.liste.some((m) => m.etat === 'depose');
      return lotEnTravail(lot) || morceauxEnEssai ? 3000 : false;
    },
  });

  const apres = (lot: LotReprise): void => {
    setErreur(null);
    client.setQueryData(cle, lot);
    void client.invalidateQueries({ queryKey: cleLots(idGalerie) });
  };
  const echec = (cause: unknown): void => {
    setErreur(messageReprise(t, cause, requete.data?.nature === 'images' ? 'images' : undefined));
    // Le lot a pu changer sous nos yeux (`lot_modifie`, `lot_non_pret`).
    void client.invalidateQueries({ queryKey: cle });
  };

  const essayer = useMutation({
    mutationFn: () => essayerLot(idGalerie, lotId),
    onSuccess: apres,
    onError: echec,
  });
  const lancer = useMutation({
    mutationFn: (lot: LotReprise) =>
      exigeSecondFacteur(lot)
        ? elevation.executer(() => lancerLot(idGalerie, lotId))
        : lancerLot(idGalerie, lotId),
    onSuccess: (lot) => {
      setConfirmation(false);
      apres(lot);
    },
    onError: (cause) => {
      setConfirmation(false);
      echec(cause);
    },
  });
  const relancer = useMutation({
    mutationFn: (lot: LotReprise) =>
      exigeSecondFacteur(lot)
        ? elevation.executer(() => relancerLot(idGalerie, lotId))
        : relancerLot(idGalerie, lotId),
    onSuccess: apres,
    onError: echec,
  });

  const retour = (
    <p>
      <Link className="lien-cible" to={{ search: '' }}>
        {t('reprise.fiche.retour')}
      </Link>
    </p>
  );

  if (requete.isLoading) return <p className={pageStyles.videEtat}>{t('reprise.chargement')}</p>;
  if (requete.isError || !requete.data) {
    return (
      <section>
        <p className={pageStyles.videEtat}>{t('reprise.fiche.introuvable')}</p>
        {retour}
      </section>
    );
  }

  const lot = requete.data;
  if (estLotImages(lot)) {
    return (
      <section className={styles.pile}>
        {retour}
        <LotImages
          idGalerie={idGalerie}
          lot={lot}
          onLot={apres}
          onEssaiLance={() => setEssaiLance(true)}
        />
        <AnnulationBloc idGalerie={idGalerie} lot={lot} onLot={apres} />
      </section>
    );
  }
  // Un lot de documents ou de factures lu dans un dépôt se mène comme un envoi.
  if (lot.origine === 'depot' && !estLotFichiers(lot)) {
    return (
      <section className={styles.pile}>
        {retour}
        <LotCorrespondance
          idGalerie={idGalerie}
          lot={lot}
          onLot={apres}
          onEssaiLance={() => setEssaiLance(true)}
        />
        <AnnulationBloc idGalerie={idGalerie} lot={lot} onLot={apres} />
      </section>
    );
  }
  const peutAgir = peut('console:run-import');
  const avantLancement = ETATS_AVANT_LANCEMENT.includes(lot.etat);
  const essaiEnCours = lot.etat === 'depose' && lotEnTravail(lot);
  const enAction = essayer.isPending || lancer.isPending || relancer.isPending;
  const nature = t(`reprise.natures.${lot.nature}`);
  const etatLot = etatAfficheLot(lot);
  const annule = lotAnnule(lot);
  // « Lancer » n'est actif qu'à l'état « prêt » : jamais sur un essai refusé.
  const lancerActif = lot.etat === 'pret' && !enAction;

  return (
    <section className={styles.pile}>
      {retour}
      {lot.parentId && lot.morceau ? (
        <p>
          <Link className="lien-cible" to={`?lot=${encodeURIComponent(lot.parentId)}`}>
            {t('correspondance.morceaux.retourParent')}
          </Link>
        </p>
      ) : null}
      <div className={pageStyles.panneau}>
        <div className={pageStyles.sectionTitre}>
          <h2>
            {lot.morceau
              ? t('correspondance.morceaux.titreFiche', { nature, rang: lot.morceau.rang })
              : t('reprise.fiche.titre', { nature })}
          </h2>
          <Badge tone={etatLot.ton}>{t(etatLot.cle)}</Badge>
        </div>
        <div className={styles.pile}>
          <p className={styles.discret}>
            {t('reprise.fiche.meta', {
              date: formaterDate(lot.creeLe, '—'),
              operateur: lot.operateur,
            })}
          </p>

          <h3 className={styles.sousTitre}>{t('reprise.fiche.sources')}</h3>
          <ul className={styles.sources}>
            {lot.sources
              // L'index du manifeste du dépôt n'est pas un fichier que la personne a choisi.
              .filter((source) => !(estLotFichiers(lot) && source.champ === 'manifeste'))
              .map((source) => (
                <li key={source.champ}>
                  <span>
                    {t(`reprise.depot.champs.${source.champ}`, { defaultValue: source.champ })} ·{' '}
                    {source.nom}
                  </span>
                  <span>{formaterOctets(source.octets)}</span>
                </li>
              ))}
            {estLotFichiers(lot) ? (
              <li>
                <span>{t('images.fiche.luDansDepot')}</span>
                <span />
              </li>
            ) : null}
            <li>
              <span>{t('reprise.fiche.lignes')}</span>
              <span>{lot.lignes}</span>
            </li>
            {lot.options.confidentiels ? (
              <li>
                <span>{t('reprise.fiche.confidentielsOui')}</span>
                <span />
              </li>
            ) : null}
            {lot.options.motifInventaire ? (
              <li>
                <span>
                  {t('reprise.fiche.motifInventaire', { motif: lot.options.motifInventaire })}
                </span>
                <span />
              </li>
            ) : null}
          </ul>

          {erreur ? (
            <p className={styles.erreur} role="alert">
              {erreur}
            </p>
          ) : null}

          {peutAgir && (avantLancement || (lot.etat === 'echoue' && !annule)) && !lotScelle(lot) ? (
            <div className={styles.actions}>
              {avantLancement ? (
                <>
                  <Button
                    variant={lot.etat === 'pret' ? 'secondary' : 'primary'}
                    loading={essayer.isPending}
                    disabled={essaiEnCours || enAction}
                    onClick={() => essayer.mutate()}
                  >
                    {essaiEnCours
                      ? t('reprise.fiche.essaiEnCours')
                      : t(
                          lot.etat === 'depose'
                            ? 'reprise.fiche.essayer'
                            : 'reprise.fiche.essayerDeNouveau',
                        )}
                  </Button>
                  <Button disabled={!lancerActif} onClick={() => setConfirmation(true)}>
                    {t('reprise.fiche.lancer')}
                  </Button>
                </>
              ) : (
                <Button loading={relancer.isPending} onClick={() => relancer.mutate(lot)}>
                  {t('reprise.fiche.relancer')}
                </Button>
              )}
            </div>
          ) : null}
          {avantLancement && lot.etat !== 'pret' && lot.etat !== 'essaye' ? (
            <p className={styles.discret}>{t('reprise.fiche.lancerAide')}</p>
          ) : null}
          {lot.etat === 'essaye' ? (
            <p className={styles.avertissement} role="note">
              {lot.parentId
                ? t('correspondance.morceaux.corrigerAide')
                : t('reprise.fiche.corrigerAide')}
            </p>
          ) : null}
          <EcartementBloc idGalerie={idGalerie} lot={lot} peutAgir={peutAgir} onLot={apres} />
        </div>
      </div>

      {lot.travail && (lot.lanceLe !== null || lotEnTravail(lot) || lot.travail.raison) ? (
        <div className={pageStyles.panneau}>
          <div className={pageStyles.sectionTitre}>
            <h2>{t('reprise.fiche.suivi')}</h2>
          </div>
          <div className={styles.pile}>
            <p>
              <Badge tone={etatLot.ton}>{t(etatLot.cle)}</Badge>{' '}
              {t('reprise.fiche.tentative', {
                tentatives: lot.travail.tentatives,
                max: lot.travail.maxTentatives,
              })}
            </p>
            <ProgressionTravail
              travail={lot.travail}
              actif={lotEnTravail(lot) && lot.annulation?.etat !== 'en_cours'}
            />
            {lot.travail.raison ? (
              <p className={styles.avertissement} role="note">
                {t('plan.progression.raison', { texte: texteRaisonTravail(t, lot.travail) })}
              </p>
            ) : null}
            {lotEnTravail(lot) ? (
              <p className={styles.discret} aria-live="polite">
                {t('reprise.fiche.rafraichi')}
              </p>
            ) : null}
            {lot.etat === 'echoue' && !annule ? (
              <p className={styles.erreur} role="alert">
                {t('reprise.fiche.echec')}
              </p>
            ) : null}
            {lot.etat === 'termine' && !lot.annulation ? (
              <p className={styles.discret}>{t('reprise.fiche.termine')}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      <AnnulationBloc idGalerie={idGalerie} lot={lot} onLot={apres} />

      {lot.rapport ? (
        <RapportVue idGalerie={idGalerie} lotId={lot.id} revision={lot.revision} type="final" />
      ) : null}
      {lot.rapportEssai ? (
        <RapportVue idGalerie={idGalerie} lotId={lot.id} revision={lot.revision} type="essai" />
      ) : null}

      <Dialog
        open={confirmation && !elevation.enElevation}
        onOpenChange={(ouvert) => {
          if (!ouvert) setConfirmation(false);
        }}
        title={t('reprise.fiche.confirmationTitre')}
        description={
          lot.nature === 'images'
            ? t('images.fiche.confirmationTexte', { count: lot.lignes })
            : t('reprise.fiche.confirmationTexte', { count: lot.lignes, nature })
        }
        closeLabel={t('reprise.fiche.annuler')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmation(false)}>
              {t('reprise.fiche.annuler')}
            </Button>
            <Button loading={lancer.isPending} onClick={() => lancer.mutate(lot)}>
              {t('reprise.fiche.lancer')}
            </Button>
          </>
        }
      >
        {exigeSecondFacteur(lot) ? (
          <p className={styles.avertissement} role="note">
            {t('reprise.fiche.confirmationMfa')}
          </p>
        ) : null}
      </Dialog>
      {elevation.dialogue}
    </section>
  );
}
